import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { FaYoutube, FaTiktok, FaInstagram } from "react-icons/fa";
import { Send, CalendarClock, CheckCircle2, AlertCircle } from "lucide-react";

const PLATFORM_ICONS = { YouTube: FaYoutube, TikTok: FaTiktok, Instagram: FaInstagram };
const PLATFORM_KEY = { YouTube: "youtube", TikTok: "tiktok", Instagram: "instagram" };

export default function ScheduleDialog({ open, onClose, videoId, defaultCaption, defaultMode = "schedule" }) {
  const [mode, setMode] = useState(defaultMode);
  const [platforms, setPlatforms] = useState(["TikTok"]);
  const [when, setWhen] = useState("");
  const [caption, setCaption] = useState(defaultCaption);
  const [saving, setSaving] = useState(false);
  const [privacy, setPrivacy] = useState("public");
  const manualPlatforms = useMemo(() => platforms.filter((p) => p !== "YouTube"), [platforms]);
  const [channels, setChannels] = useState({});
  const [links, setLinks] = useState({ YouTube: "", TikTok: "", Instagram: "" });
  const [ytConnected, setYtConnected] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    setCaption(defaultCaption);
  }, [defaultCaption]);

  useEffect(() => {
    if (open) {
      setMode(defaultMode);
      api
        .get("/channels")
        .then((r) => {
          const c = r.data || {};
          setChannels(c);
          setLinks({ YouTube: c.youtube || "", TikTok: c.tiktok || "", Instagram: c.instagram || "" });
        })
        .catch(() => {});
      api.get("/youtube/status").then((r) => setYtConnected(!!r.data.connected)).catch(() => {});
    }
  }, [open, defaultMode]);

  const toggle = (p) =>
    setPlatforms((arr) => (arr.includes(p) ? arr.filter((x) => x !== p) : [...arr, p]));

  const save = async () => {
    if (platforms.length === 0) {
      toast.error("Select at least one platform");
      return;
    }
    if (mode === "schedule" && !when) {
      toast.error("Pick a date & time");
      return;
    }
    const wantsYouTube = platforms.includes("YouTube");
    if (wantsYouTube && !ytConnected) {
      toast.error("Connect your YouTube account in Settings first.");
      return;
    }
    setSaving(true);
    try {
      // Real upload to YouTube when publishing now.
      let ytUrl = null;
      if (wantsYouTube && mode === "now") {
        const { data: up } = await api.post(`/videos/${videoId}/youtube-upload`, { privacy });
        ytUrl = up.video_url;
      }
      // Record the post(s) in the schedule timeline (also queues YouTube for future dates → auto-published by the worker).
      await api.post("/schedule", {
        video_id: videoId,
        platforms,
        scheduled_at: mode === "schedule" ? new Date(when).toISOString() : new Date().toISOString(),
        caption,
        publish_now: mode === "now",
        privacy,
        channel_links: ytUrl ? { ...links, YouTube: ytUrl } : links,
      });
      if (ytUrl) {
        toast.success("Published to YouTube!", {
          action: { label: "Open", onClick: () => window.open(ytUrl, "_blank") },
        });
      } else if (wantsYouTube && mode === "schedule") {
        toast.success("Scheduled — we'll auto-publish to YouTube at the set time.");
      } else {
        toast.success(mode === "now" ? "Published!" : "Scheduled!");
      }
      onClose();
      navigate("/schedule");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSaving(false);
    }
  };

  const ModeBtn = ({ value, icon: Icon, label, testid }) => (
    <button
      onClick={() => setMode(value)}
      data-testid={testid}
      className={`flex items-center justify-center gap-1.5 py-2 rounded text-sm font-medium transition-colors ${
        mode === value ? "bg-blue-600 text-white" : "text-zinc-400 hover:text-white"
      }`}
    >
      <Icon className="h-4 w-4" /> {label}
    </button>
  );

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#121212] border-white/10 text-white" data-testid="schedule-dialog">
        <DialogHeader>
          <DialogTitle className="font-head">Publish to social</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-1 p-1 bg-[#0A0A0A] border border-white/10 rounded-md">
            <ModeBtn value="now" icon={Send} label="Publish now" testid="mode-now" />
            <ModeBtn value="schedule" icon={CalendarClock} label="Schedule" testid="mode-schedule" />
          </div>

          <div>
            <label className="block text-xs uppercase tracking-[0.15em] text-zinc-500 mb-2">Platforms</label>
            <div className="grid grid-cols-2 gap-2">
              {[{ name: "TikTok" }, { name: "YouTube" }].map((pf) => {
                const p = pf.name;
                const Icon = PLATFORM_ICONS[p];
                const on = platforms.includes(p);
                const isYT = p === "YouTube";
                const linked = isYT ? ytConnected : !!channels[PLATFORM_KEY[p]];
                return (
                  <button
                    key={p}
                    onClick={() => toggle(p)}
                    data-testid={`platform-${p}`}
                    className={`relative flex flex-col items-center gap-1.5 py-3 rounded-md border transition-colors ${
                      on
                        ? "border-blue-500 bg-blue-600/15 text-white"
                        : "border-white/10 text-zinc-500 hover:text-white"
                    }`}
                  >
                    <Icon className="h-5 w-5" /> <span className="text-xs">{p}</span>
                    {linked ? (
                      <span className="text-[10px] text-green-400 inline-flex items-center gap-0.5" data-testid={`platform-linked-${p}`}>
                        <CheckCircle2 className="h-2.5 w-2.5" /> {isYT ? "connected" : "linked"}
                      </span>
                    ) : (
                      <span className="text-[10px] text-amber-400/80 inline-flex items-center gap-0.5">
                        <AlertCircle className="h-2.5 w-2.5" /> {isYT ? "connect in settings" : "no link"}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Inline channel link inputs for selected platforms (YouTube uses OAuth, no manual link). */}
          {manualPlatforms.length > 0 && (
            <div className="space-y-2.5" data-testid="channel-link-inputs">
              {manualPlatforms.map((p) => {
                const Icon = PLATFORM_ICONS[p];
                return (
                  <div key={p}>
                    <label className="block text-[11px] text-zinc-500 mb-1 flex items-center gap-1.5">
                      <Icon className="h-3 w-3" /> {p} channel link
                    </label>
                    <input
                      value={links[p] || ""}
                      onChange={(e) => setLinks((l) => ({ ...l, [p]: e.target.value }))}
                      placeholder={`https://${p.toLowerCase()}.com/@yourchannel`}
                      data-testid={`schedule-link-${p}`}
                      className="w-full bg-[#0A0A0A] border border-white/10 rounded-sm px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                );
              })}
            </div>
          )}

          {platforms.includes("YouTube") && (
            <div data-testid="youtube-privacy-row">
              <label className="block text-xs uppercase tracking-[0.15em] text-zinc-500 mb-1.5">YouTube privacy</label>
              <select
                value={privacy}
                onChange={(e) => setPrivacy(e.target.value)}
                data-testid="youtube-privacy-select"
                className="w-full bg-[#0A0A0A] border border-white/10 rounded-sm px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 [color-scheme:dark]"
              >
                <option value="public">Public</option>
                <option value="unlisted">Unlisted</option>
                <option value="private">Private</option>
              </select>
            </div>
          )}

          {mode === "schedule" && (
            <div>
              <label className="block text-xs uppercase tracking-[0.15em] text-zinc-500 mb-1.5">Date & time</label>
              <input
                type="datetime-local"
                value={when}
                onChange={(e) => setWhen(e.target.value)}
                data-testid="schedule-datetime"
                className="w-full bg-[#0A0A0A] border border-white/10 rounded-sm px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 [color-scheme:dark]"
              />
            </div>
          )}

          <div>
            <label className="block text-xs uppercase tracking-[0.15em] text-zinc-500 mb-1.5">Caption</label>
            <textarea
              value={caption}
              onChange={(e) => setCaption(e.target.value)}
              rows={2}
              data-testid="schedule-caption"
              className="w-full bg-[#0A0A0A] border border-white/10 rounded-sm px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
            />
          </div>

          <button
            onClick={save}
            disabled={saving}
            data-testid="confirm-schedule-btn"
            className="w-full bg-blue-600 hover:bg-blue-500 py-2.5 rounded-md font-semibold transition-colors disabled:opacity-60"
          >
            {saving
              ? mode === "now"
                ? "Publishing…"
                : "Scheduling…"
              : mode === "now"
              ? "Publish now"
              : "Add to queue"}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
